/* 
 * DESIGN: "Leśna Przystań" — Forest Deep footer
 */
import { Link } from "wouter";
import { Phone, Mail, MapPin, Facebook, Clock, Heart } from "lucide-react";

export default function Footer() {
  return (
    <footer
      className="text-white"
      style={{ background: "oklch(0.18 0.06 155)" }}
    >
      {/* Wave top */}
      <div className="overflow-hidden leading-none">
        <svg viewBox="0 0 1440 60" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
          <path d="M0 60L60 50C120 40 240 20 360 15C480 10 600 20 720 25C840 30 960 30 1080 25C1200 20 1320 10 1380 5L1440 0V60H1380C1320 60 1200 60 1080 60C960 60 840 60 720 60C600 60 480 60 360 60C240 60 120 60 60 60H0Z"
            fill="oklch(0.97 0.02 90)" />
        </svg>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
          {/* Brand */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-3 mb-4">
              <img
                src="/manus-storage/logo_bced41d4.png"
                alt="Kajaki Kudłaty Cooky"
                loading="eager"
                decoding="async"
                className="h-12 w-12 object-contain"
              />
              <div>
                <div
                  className="font-bold text-white text-lg leading-tight"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  Kajaki Kudłaty Cooky
                </div>
                <div
                  className="text-[oklch(0.78_0.12_70)] text-xs leading-tight"
                  style={{ fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.1em", textTransform: "uppercase" }}
                >
                  Zakościele · Pilica
                </div>
              </div>
            </div>
            <p className="text-white/70 text-sm leading-relaxed mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Pilica, kajak, Twój pies. I ognisko na metę. Najtańsze spływy kajakowe nad Pilicą z gwarancją ceny.
            </p>
            <a
              href="https://www.facebook.com/profile.php?id=61575323100598"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-white/70 hover:text-[oklch(0.62_0.18_45)] transition-colors text-sm"
            >
              <Facebook size={18} />
              Facebook
            </a>
          </div>

          {/* Quick links */}
          <div>
            <h3
              className="text-[oklch(0.78_0.12_70)] font-semibold mb-4 text-sm"
              style={{ fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.15em", textTransform: "uppercase" }}
            >
              Nawigacja
            </h3>
            <ul className="space-y-2">
              {[
                { href: "/splyw", label: "Spływy kajakowe" },
                { href: "/cennik", label: "Cennik" },
                { href: "/z-psem", label: "Z psem na kajak" },
                { href: "/grupy-i-eventy", label: "Grupy & Eventy" },
                { href: "/ognisko", label: "Ognisko po spływie" },
                { href: "/galeria", label: "Galeria" },
                { href: "/o-nas", label: "O nas" },
                { href: "/rezerwacja", label: "Rezerwacja" },
              ].map((link) => (
                <li key={link.href}>
                  <Link
                    href={link.href}
                    className="text-white/70 hover:text-[oklch(0.62_0.18_45)] transition-colors text-sm"
                    style={{ fontFamily: "'Outfit', sans-serif" }}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3
              className="text-[oklch(0.78_0.12_70)] font-semibold mb-4 text-sm"
              style={{ fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.15em", textTransform: "uppercase" }}
            >
              Kontakt
            </h3>
            <ul className="space-y-3">
              <li>
                <a
                  href="tel:+48787028770"
                  className="flex items-start gap-3 text-white/70 hover:text-[oklch(0.62_0.18_45)] transition-colors text-sm"
                >
                  <Phone size={16} className="flex-shrink-0 mt-0.5" />
                  <span style={{ fontFamily: "'Outfit', sans-serif" }}>+48 787 028 770</span>
                </a>
              </li>
              <li>
                <a
                  href="mailto:kontakt@kajakikudlatycooky.pl"
                  className="flex items-start gap-3 text-white/70 hover:text-[oklch(0.62_0.18_45)] transition-colors text-sm"
                >
                  <Mail size={16} className="flex-shrink-0 mt-0.5" />
                  <span style={{ fontFamily: "'Outfit', sans-serif" }}>kontakt@kajakikudlatycooky.pl</span>
                </a>
              </li>
              <li>
                <div className="flex items-start gap-3 text-white/70 text-sm">
                  <MapPin size={16} className="flex-shrink-0 mt-0.5" />
                  <span style={{ fontFamily: "'Outfit', sans-serif" }}>
                    Zakościele 60c<br />
                    97-215 Zakościele<br />
                    (gmina Inowłódz)
                  </span>
                </div>
              </li>
              <li>
                <div className="flex items-start gap-3 text-white/70 text-sm">
                  <Clock size={16} className="flex-shrink-0 mt-0.5" />
                  <div style={{ fontFamily: "'Outfit', sans-serif" }}>
                    <div>Pn–Pt: 9:00–19:00</div>
                    <div>Sb–Nd: 8:00–20:00</div>
                  </div>
                </div>
              </li>
            </ul>
          </div>

          {/* Lokalizacje */}
          <div>
            <h3
              className="text-[oklch(0.78_0.12_70)] font-semibold mb-4 text-sm"
              style={{ fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.15em", textTransform: "uppercase" }}
            >
              Lokalizacje
            </h3>
            <ul className="space-y-2">
              {[
                { href: "/kajaki-spala", label: "Kajaki Spała" },
                { href: "/kajaki-inowlodz", label: "Kajaki Inowłódz" },
                { href: "/kajaki-tomaszow-mazowiecki", label: "Kajaki Tomaszów Maz." },
                { href: "/kajaki-smardzewice", label: "Kajaki Smardzewice" },
                { href: "/kajaki-zakosciele", label: "Kajaki Zakościele" },
                { href: "/kajaki-gapinin", label: "Kajaki Gapinin" },
                { href: "/kajaki-domaniewice", label: "Kajaki Domaniewice" },
                { href: "/kajaki-nowe-miasto", label: "Kajaki Nowe Miasto" },
                { href: "/kajaki-mysiakowiec", label: "Kajaki Mysiakowiec" },
              ].map((link) => (
                <li key={link.href}>
                  <Link
                    href={link.href}
                    className="text-white/70 hover:text-[oklch(0.62_0.18_45)] transition-colors text-sm"
                    style={{ fontFamily: "'Outfit', sans-serif" }}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* USP + Sedeka */}
          <div>
            <h3
              className="text-[oklch(0.78_0.12_70)] font-semibold mb-4 text-sm"
              style={{ fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.15em", textTransform: "uppercase" }}
            >
              Nasze wyróżniki
            </h3>
            <ul className="space-y-2 text-sm text-white/70 mb-6" style={{ fontFamily: "'Outfit', sans-serif" }}>
              <li className="flex items-center gap-2">
                <span className="text-[oklch(0.62_0.18_45)]">✓</span>
                Najtańsze ceny w okolicy
              </li>
              <li className="flex items-center gap-2">
                <span className="text-[oklch(0.62_0.18_45)]">✓</span>
                Gwarancja ceny — 5% taniej
              </li>
              <li className="flex items-center gap-2">
                <span className="text-[oklch(0.62_0.18_45)]">✓</span>
                Kajaki z psem (kapoki dla psów)
              </li>
              <li className="flex items-center gap-2">
                <span className="text-[oklch(0.62_0.18_45)]">✓</span>
                Ognisko gratis + muzyka
              </li>
              <li className="flex items-center gap-2">
                <span className="text-[oklch(0.62_0.18_45)]">✓</span>
                Nowy, zadbany sprzęt
              </li>
              <li className="flex items-center gap-2">
                <span className="text-[oklch(0.62_0.18_45)]">✓</span>
                Transport z Tomaszowa Maz.
              </li>
            </ul>

            {/* Sedeka */}
            <div
              className="p-3 rounded-lg border border-white/10"
              style={{ background: "oklch(0.25 0.06 155)" }}
            >
              <div className="flex items-center gap-2 mb-1">
                <Heart size={14} className="text-red-400" />
                <span
                  className="text-xs font-semibold text-white/80"
                  style={{ fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.1em", textTransform: "uppercase" }}
                >
                  Wspieramy
                </span>
              </div>
              <p className="text-xs text-white/60" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Fundację Sedeka <strong className="text-white/80">"Zdążyć z pomocą"</strong>
              </p>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-10 pt-6 border-t border-white/10 flex flex-col items-center gap-2">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 w-full">
            <p
              className="text-white/60 text-xs"
              style={{ fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.08em" }}
            >
              © 2026 Kajaki Kudłaty Cooky. Wszelkie prawa zastrzeżone.
            </p>
            <p
              className="text-white/60 text-xs"
              style={{ fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.08em" }}
            >
              Zakościele · Inowłódz · Pilica
            </p>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1">
            <Link
              href="/polityka-prywatnosci"
              className="text-white/60 hover:text-white/90 text-xs underline underline-offset-2 transition-colors"
              style={{ fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.08em" }}
            >
              Polityka prywatności
            </Link>
            <button
              onClick={() => { localStorage.removeItem('cookie_consent_v1'); window.location.reload(); }}
              className="text-white/60 hover:text-white/90 text-xs underline underline-offset-2 transition-colors"
              style={{ fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.08em" }}
            >
              Ustawienia cookies
            </button>
          </div>
          <p
            className="text-white/60 text-xs"
            style={{ fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.08em" }}
          >
            Powered by:{" "}
            <a
              href="https://architekcibiznesu.pl/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-white/50 hover:text-white/80 transition-colors underline underline-offset-2"
            >
              Architekci Biznesu
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
}
